import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { message } from 'antd';
import { MessageType } from 'antd/es/message/interface';
import { ROUTES } from '@/utils/pathRoutes';
import { isVanHoaPath } from '@/utils/vanHoaHuongDan';

/** Chỉ hiện toast nếu chuyển trang > delay (tránh nhấp nháy trên navigation nhanh). */
const LOADING_SHOW_DELAY_MS = 400;
const LOADING_TIMEOUT_MS = 30_000;
const ROUTE_LOADING_MESSAGE = 'Đang tải dữ liệu...';

const isIntroPath = (path: string) => {
  const normalized = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return normalized === ROUTES.INTRO.replace(/\/$/, '');
};

const normalizePath = (path: string) => path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

/** Intro: hiện loading ngay. VH↔VH: instant. BSC giữ delay 400ms như cũ. */
const resolveLoadingDelayMs = (currentPath: string, targetUrl: string) => {
  if (isIntroPath(currentPath)) return 0;
  if (isVanHoaPath(currentPath) && isVanHoaPath(targetUrl)) return 0;
  return LOADING_SHOW_DELAY_MS;
};

type AppLoadingContextType = {
  showLoading: (title?: string) => void;
  hideLoading: () => void;
};

const AppLoadingContext = createContext<AppLoadingContextType | undefined>(undefined);

export const AppLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  const hideLoadingRef = useRef<MessageType | null>(null);
  const showDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearShowDelayTimer = () => {
    if (showDelayTimerRef.current) {
      clearTimeout(showDelayTimerRef.current);
      showDelayTimerRef.current = null;
    }
  };

  const clearLoadingTimer = () => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
  };

  const hideLoading = () => {
    clearShowDelayTimer();
    clearLoadingTimer();
    if (hideLoadingRef.current) {
      hideLoadingRef.current();
      hideLoadingRef.current = null;
    }
  };

  const showLoading = (title = ROUTE_LOADING_MESSAGE) => {
    if (hideLoadingRef.current) {
      hideLoadingRef.current();
      hideLoadingRef.current = null;
    }
    hideLoadingRef.current = message.loading(title, 0);
  };

  const hideLoadingRefFn = useRef(hideLoading);
  hideLoadingRefFn.current = hideLoading;
  const showLoadingRefFn = useRef(showLoading);
  showLoadingRefFn.current = showLoading;

  useEffect(() => {
    const handleStart = (url: string) => {
      const currentPath = routerRef.current.asPath;
      if (normalizePath(url) === normalizePath(currentPath)) return;

      // Rời /intro: chuyển phân hệ ngay, không hiện toast loading
      if (isIntroPath(currentPath)) return;

      hideLoadingRefFn.current();
      clearShowDelayTimer();
      clearLoadingTimer();

      const delayMs = resolveLoadingDelayMs(currentPath, url);

      showDelayTimerRef.current = setTimeout(() => {
        showLoadingRefFn.current(ROUTE_LOADING_MESSAGE);
        loadingTimerRef.current = setTimeout(() => {
          hideLoadingRefFn.current();
        }, LOADING_TIMEOUT_MS);
      }, delayMs);
    };

    const handleStop = () => hideLoadingRefFn.current();

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      hideLoadingRefFn.current();
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
    };
  }, [router.events]);

  useEffect(() => {
    if (!router.isReady) return;
    hideLoadingRefFn.current();
  }, [router.asPath, router.isReady]);

  return <AppLoadingContext.Provider value={{ showLoading, hideLoading }}>{children}</AppLoadingContext.Provider>;
};

export const useLoading = () => {
  const ctx = useContext(AppLoadingContext);
  if (!ctx) throw new Error('useLoading must be used within LoadingProvider');
  return ctx;
};
